import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useAdminTheme } from '../../context/AdminThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Plus, Trash2, Package, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_BASE = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000').replace(/\/$/, '');
const API = `${BACKEND_BASE}/api`;

const ALLOWED_CATEGORIES = ['Paddles', 'Balls', 'Footwear', 'Accessories', 'Equipment'];

// If production Nginx has a small client_max_body_size (often 1MB), we must keep
// uploads below that threshold or they'll be rejected before reaching FastAPI.
const TARGET_UPLOAD_MAX_BYTES = 700 * 1024; // ~700KB (conservative for 1MB Nginx limit)
const TARGET_IMAGE_MAX_DIMENSION = 1600;

const formatCurrency = (amount) => {
  return `₱${Number(amount).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const resolveImageUrl = (image) => {
  if (!image) return '';
  if (image.startsWith('http')) return image;
  return `${BACKEND_BASE}${image}`;
};

const getWebSocketUrl = () => {
  // BACKEND_BASE is http(s)://...; convert to ws(s)://...
  const wsBase = BACKEND_BASE.replace(/^http/i, 'ws');
  return `${wsBase}/api/ws`;
};

const loadImageElement = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    URL.revokeObjectURL(url);
    resolve(img);
  };
  img.onerror = (err) => {
    URL.revokeObjectURL(url);
    reject(err);
  };
  img.src = url;
});

const canvasToBlob = (canvas, type, quality) => new Promise((resolve) => {
  canvas.toBlob((blob) => resolve(blob), type, quality);
});

const compressImageIfNeeded = async (file) => {
  // Keep gifs as-is (canvas conversion breaks animation).
  if (!file || file.type === 'image/gif') return { file, changed: false };
  if (file.size <= TARGET_UPLOAD_MAX_BYTES) return { file, changed: false };

  try {
    const img = await loadImageElement(file);
    const srcWidth = img.naturalWidth || img.width;
    const srcHeight = img.naturalHeight || img.height;
    if (!srcWidth || !srcHeight) return { file, changed: false };

    const scale = Math.min(1, TARGET_IMAGE_MAX_DIMENSION / Math.max(srcWidth, srcHeight));
    const outWidth = Math.max(1, Math.round(srcWidth * scale));
    const outHeight = Math.max(1, Math.round(srcHeight * scale));

    const canvas = document.createElement('canvas');
    canvas.width = outWidth;
    canvas.height = outHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return { file, changed: false };

    // White background to avoid black fill when converting transparent PNG -> JPEG.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, outWidth, outHeight);
    ctx.drawImage(img, 0, 0, outWidth, outHeight);

    // Iteratively lower JPEG quality until we hit target size.
    const outType = 'image/jpeg';
    let quality = 0.80;
    let blob = await canvasToBlob(canvas, outType, quality);
    while (blob && blob.size > TARGET_UPLOAD_MAX_BYTES && quality > 0.3) {
      quality -= 0.08;
      blob = await canvasToBlob(canvas, outType, quality);
    }

    if (!blob) return { file, changed: false };
    if (blob.size > file.size) return { file, changed: false };

    const baseName = (file.name || 'upload').replace(/\.[^/.]+$/, '');
    const newFile = new File([blob], `${baseName}.jpg`, { type: outType });
    return { file: newFile, changed: true };
  } catch {
    return { file, changed: false };
  }
};

const AdminEquipmentPage = () => {
  const { token, hasRole } = useAdminAuth();
  const { isDarkMode, theme } = useAdminTheme();
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  // Synchronous guard to prevent double-submit before React state updates.
  const submitGuardRef = useRef(false);
  const idempotencyKeyRef = useRef(null);

  const [saving, setSaving] = useState(false);
  const [newEquipment, setNewEquipment] = useState({
    name: '',
    price: '',
    category: '',
    description: '',
    available_quantity: '1',
    image: '',
    file: null,
  });
  const [errors, setErrors] = useState({ name: '', price: '', category: '', image: '' });
  const [touched, setTouched] = useState({ name: false, price: false, category: false, image: false });
  const [formAlert, setFormAlert] = useState({ type: '', message: '' });

  const fetchEquipment = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/equipment`, { headers: { 'Authorization': `Bearer ${token}` }});
      if (res.ok) setEquipment(await res.json());
    } catch (error) { console.error('Failed:', error); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    fetchEquipment();
  }, [fetchEquipment]);

  // Realtime updates via WebSocket (auto-reconnect).
  useEffect(() => {
    if (!token) return;

    let ws;
    let reconnectTimer;
    let reconnectDelayMs = 2000;

    const connect = () => {
      ws = new WebSocket(getWebSocketUrl());

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (!msg || typeof msg !== 'object') return;

          if (msg.type === 'equipment_created' && msg.equipment?.equipment_id) {
            const created = msg.equipment;
            setEquipment((prev) => {
              if (prev.some((e) => e.equipment_id === created.equipment_id)) return prev;
              return [created, ...prev];
            });
          }

          if (msg.type === 'equipment_updated' && msg.equipment?.equipment_id) {
            const updated = msg.equipment;
            setEquipment((prev) => prev.map((e) => (e.equipment_id === updated.equipment_id ? updated : e)));
          }

          if (msg.type === 'equipment_deleted' && msg.equipment_id) {
            const id = msg.equipment_id;
            setEquipment((prev) => prev.filter((e) => e.equipment_id !== id));
          }
        } catch {
          // ignore malformed messages
        }
      };

      ws.onopen = () => {
        reconnectDelayMs = 2000;
      };

      ws.onclose = () => {
        if (reconnectTimer) clearTimeout(reconnectTimer);
        reconnectTimer = setTimeout(() => {
          reconnectDelayMs = Math.min(reconnectDelayMs * 2, 30000);
          connect();
        }, reconnectDelayMs);
      };

      ws.onerror = () => {
        try { ws.close(); } catch { /* ignore */ }
      };
    };

    connect();
    return () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { ws?.close(); } catch { /* ignore */ }
    };
  }, [token]);

  const validateField = (field, value) => {
    switch (field) {
      case 'name':
        return value.trim() ? '' : 'Item name is required';
      case 'price': {
        if (!value) return 'Price is required';
        const num = parseFloat(value);
        if (Number.isNaN(num) || num <= 0) return 'Price must be greater than 0';
        return '';
      }
      case 'category':
        return value ? '' : 'Category is required';
      case 'image':
        return newEquipment.file || newEquipment.image ? '' : 'Image is required';
      default:
        return '';
    }
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field, newEquipment[field]);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleInputChange = (field, value) => {
    setNewEquipment((prev) => ({ ...prev, [field]: value }));
    if (touched[field]) {
      const error = validateField(field, value);
      setErrors((prev) => ({ ...prev, [field]: error }));
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];

    if (file.size > 14 * 1024 * 1024) {
      const msg = 'Image size must be less than 14MB';
      setErrors((prev) => ({ ...prev, image: msg }));
      setFormAlert({ type: 'error', message: msg });
      setTouched((prev) => ({ ...prev, image: true }));
      return;
    }

    if (!allowedTypes.includes(file.type)) {
      const msg = 'Invalid file type. Allowed formats: JPG, PNG, GIF';
      setErrors((prev) => ({ ...prev, image: msg }));
      setFormAlert({ type: 'error', message: msg });
      setTouched((prev) => ({ ...prev, image: true }));
      if (e?.target) e.target.value = '';
      return;
    }

    setErrors((prev) => ({ ...prev, image: '' }));
    setFormAlert({ type: '', message: '' });

    const { file: uploadFile, changed } = await compressImageIfNeeded(file);
    const sizeMB = (uploadFile.size / (1024 * 1024)).toFixed(2);
    
    if (changed) {
      toast.success(`Image optimized to ${sizeMB}MB for upload`);
    } else if (uploadFile.size > TARGET_UPLOAD_MAX_BYTES) {
      toast.warning(`Image is ${sizeMB}MB. Server may reject uploads >1MB.`);
    }

    const previewUrl = URL.createObjectURL(uploadFile);
    setNewEquipment((prev) => ({ ...prev, image: previewUrl, file: uploadFile }));
  };

  const handleAddEquipment = async (e) => {
    e.preventDefault();

    const nameError = validateField('name', newEquipment.name);
    const priceError = validateField('price', newEquipment.price);
    const categoryError = validateField('category', newEquipment.category);
    const imageError = validateField('image', newEquipment.image);

    setErrors({ name: nameError, price: priceError, category: categoryError, image: imageError });
    setTouched({ name: true, price: true, category: true, image: true });

    if (nameError || priceError || categoryError || imageError || saving) return;
    if (submitGuardRef.current) return;
    submitGuardRef.current = true;
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }

    try {
      setSaving(true);
      setFormAlert({ type: '', message: '' });

      const formData = new FormData();
      formData.append('name', newEquipment.name);
      formData.append('price', String(parseFloat(newEquipment.price) || 0));
      formData.append('category', newEquipment.category);
      formData.append('description', newEquipment.description || '');
      formData.append('available_quantity', String(parseInt(newEquipment.available_quantity || '0', 10) || 0));
      if (newEquipment.file) {
        formData.append('image', newEquipment.file);
      }

      // Use the same public equipment creation route (multipart upload w/ image)
      const response = await fetch(`${API}/equipment`, {
        method: 'POST',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          // Helps prevent duplicate inserts if the client retries.
          'X-Idempotency-Key': idempotencyKeyRef.current,
        },
        body: formData,
      });

      if (!response.ok) {
        if (response.status === 405) {
          throw new Error('Backend does not allow POST /api/equipment (405). Restart the backend so the latest routes load.');
        }
        let errorMessage = `Failed to save equipment (${response.status}${response.statusText ? ` ${response.statusText}` : ''})`;
        if (response.status === 413) {
          errorMessage = 'Upload rejected (413). The server/proxy is limiting upload size.';
        }
        const contentType = response.headers.get('content-type') || '';
        try {
          if (contentType.includes('application/json')) {
            const body = await response.json();
            if (body?.detail) {
              if (typeof body.detail === 'string') errorMessage = body.detail;
              else if (Array.isArray(body.detail)) errorMessage = body.detail.map((d) => (typeof d === 'string' ? d : d?.msg || '')).filter(Boolean).join('\n') || errorMessage;
            } else if (body?.error) {
              errorMessage = body.error;
            }
          } else {
            const text = await response.text();
            if (text && text.trim()) {
              errorMessage = response.status === 413
                ? `Upload rejected (413). The server/proxy is limiting upload size. ${text.trim()}`
                : text.trim();
            }
          }
        } catch {
          // ignore parse errors
        }
        throw new Error(errorMessage);
      }

      const created = await response.json();
      toast.success('Equipment saved successfully');
      setShowAddModal(false);
      setNewEquipment({ name: '', price: '', category: '', description: '', available_quantity: '1', image: '', file: null });
      setErrors({ name: '', price: '', category: '', image: '' });
      setTouched({ name: false, price: false, category: false, image: false });
      setFormAlert({ type: '', message: '' });
      // Refresh list to ensure a single entry even if a transient duplicate render occurred.
      fetchEquipment();
      // WebSocket broadcast will add it to the list within milliseconds (no optimistic insert needed).
    } catch (error) {
      const msg = error?.message || 'Failed to save equipment. Please try again.';
      console.error('Error saving equipment:', error);
      setFormAlert({ type: 'error', message: msg });
      toast.error(msg);
    } finally {
      setSaving(false);
      submitGuardRef.current = false;
      idempotencyKeyRef.current = null;
    }
  };

  const deleteEquipment = async (equipmentId) => {
    if (!confirm('Delete?')) return;
    try {
      const res = await fetch(`${API}/admin/equipment/${equipmentId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }});
      if (!res.ok) throw new Error('Failed');
      toast.success('Deleted');
      setEquipment((prev) => prev.filter((item) => item.equipment_id !== equipmentId));
    } catch (error) { toast.error('Failed'); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${theme.text}`}>Equipment</h1>
            <p className={`${theme.textMuted} mt-1`}>Manage rental equipment</p>
          </div>
          {hasRole(['super_admin', 'manager']) && (
            <Button onClick={() => setShowModal(true)} className="bg-emerald-600">
              <Plus className="h-4 w-4 mr-2" /> Add Equipment
            </Button>
          )}
        </div>

        {loading ? (
          <div className={`animate-spin rounded-full h-8 w-8 border-b-2 mx-auto ${isDarkMode ? 'border-emerald-500' : 'border-emerald-600'}`}></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {equipment.map((item) => (
              <Card key={item.equipment_id} className={`${theme.card}`}>
                <CardContent className="p-4">
                  <div className={`aspect-square rounded-lg mb-3 flex items-center justify-center overflow-hidden ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
                    {item.image ? <img src={item.image} alt="" className="w-full h-full object-cover" /> : <Package className={`h-12 w-12 ${isDarkMode ? 'text-slate-600' : 'text-gray-400'}`} />}
                  </div>
                  <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{item.name}</h3>
                  <p className={`text-xs mb-2 ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>{item.category}</p>
                  <div className="flex justify-between text-sm mb-2">
                    <span className={isDarkMode ? 'text-slate-400' : 'text-gray-600'}>₱{item.price_per_hour}/hr</span>
                    <span className={isDarkMode ? 'text-slate-400' : 'text-gray-600'}>₱{item.price_per_day}/day</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={`text-xs ${theme.textSubtle}`}>{item.available_quantity} available</span>
                    {hasRole(['super_admin', 'manager']) && (
                      <Button size="sm" variant="ghost" className="text-rose-400 hover:text-rose-300" onClick={() => deleteEquipment(item.equipment_id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className={`w-full max-w-md ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
              <CardHeader><CardTitle className={isDarkMode ? 'text-white' : 'text-gray-900'}>Add Equipment</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>Name</Label>
                  <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} className={isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'} />
                </div>
                <div>
                  <Label className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>Category</Label>
                  <select value={form.category} onChange={(e) => setForm(p => ({ ...p, category: e.target.value }))} className={`w-full h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>
                    <option>Paddles</option><option>Balls</option><option>Shoes</option><option>Accessories</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>Price/Hour</Label><Input type="number" value={form.price_per_hour} onChange={(e) => setForm(p => ({ ...p, price_per_hour: parseFloat(e.target.value) }))} className={isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'} /></div>
                  <div><Label className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>Price/Day</Label><Input type="number" value={form.price_per_day} onChange={(e) => setForm(p => ({ ...p, price_per_day: parseFloat(e.target.value) }))} className={isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'} /></div>
                </div>
                <div><Label className={isDarkMode ? 'text-slate-300' : 'text-gray-700'}>Quantity</Label><Input type="number" value={form.available_quantity} onChange={(e) => setForm(p => ({ ...p, available_quantity: parseInt(e.target.value) }))} className={isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'} /></div>
                <div className="flex gap-2 pt-4">
                  <Button variant="outline" className={`flex-1 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`} onClick={() => setShowModal(false)}>Cancel</Button>
                  <Button className="flex-1 bg-emerald-600" onClick={createEquipment}>Create</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminEquipmentPage;
